import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useBranch } from '@/features/branch/branch-context';
import { api } from '@/lib/api';
import type {
  AlertStatusFilter,
  OwnThresholds,
  ReadingAlert,
  ReadingAlertList,
  ReadingKind,
  ReadingsPage,
  ThresholdsView,
} from './types';

const LIVE_MS = 30_000;
const all = ['home-readings'] as const;

function useBranchKey() {
  const { selected, canSelectAll } = useBranch();
  return selected?.id ?? (canSelectAll ? 'all' : 'none');
}

/** GET /chronic/reading-alerts?status&mine: the branch care team's alerts. */
export function useReadingAlerts(status: AlertStatusFilter, mine: boolean) {
  const branch = useBranchKey();
  return useQuery({
    queryKey: [...all, 'alerts', branch, status, mine],
    queryFn: async ({ signal }) =>
      (
        await api.get<ReadingAlertList>('/chronic/reading-alerts', {
          signal,
          query: { status, ...(mine ? { mine: 'true' } : {}) },
        })
      ).data,
    enabled: branch !== 'none',
    refetchInterval: LIVE_MS,
  });
}

/** POST /chronic/reading-alerts/{id}/acknowledge|resolve {version, note}. */
export function useAlertDecision(
  id: string,
  action: 'acknowledge' | 'resolve',
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { version: number; note?: string | null }) =>
      (
        await api.post<ReadingAlert>(
          `/chronic/reading-alerts/${id}/${action}`,
          body,
        )
      ).data,
    onSettled: () =>
      void qc.invalidateQueries({ queryKey: [...all, 'alerts'] }),
  });
}

/** GET /chronic/home-readings/{patientId}?kind (cursor pages, newest first). */
export function useHomeReadings(patientId: string, kind: ReadingKind) {
  const branch = useBranchKey();
  return useInfiniteQuery({
    queryKey: [...all, 'readings', branch, patientId, kind],
    initialPageParam: null as string | null,
    queryFn: async ({ signal, pageParam }) =>
      (
        await api.get<ReadingsPage>(`/chronic/home-readings/${patientId}`, {
          signal,
          query: { kind, ...(pageParam ? { cursor: pageParam } : {}) },
        })
      ).data,
    getNextPageParam: (last) => (last.hasMore ? last.nextCursor : null),
    enabled: branch !== 'none',
  });
}

/** GET /chronic/reading-thresholds/{patientId}: own, defaults, effective. */
export function useReadingThresholds(patientId: string, enabled = true) {
  const branch = useBranchKey();
  return useQuery({
    queryKey: [...all, 'thresholds', branch, patientId],
    queryFn: async ({ signal }) =>
      (
        await api.get<ThresholdsView>(
          `/chronic/reading-thresholds/${patientId}`,
          { signal },
        )
      ).data,
    enabled: enabled && branch !== 'none',
  });
}

/** PUT /chronic/reading-thresholds/{patientId} {version, ...four values}. */
export function useSaveThresholds(patientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: OwnThresholds & { version: number }) =>
      (
        await api.put<ThresholdsView>(
          `/chronic/reading-thresholds/${patientId}`,
          body,
        )
      ).data,
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: [...all, 'thresholds'] });
      void qc.invalidateQueries({ queryKey: [...all, 'readings'] });
    },
  });
}
